/** @format */

import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { TOrgIndexEntry } from "../utils/layout";
import { measureCardHeights } from "../utils/measureCards";

interface TFontFaceSet {
  ready: Promise<unknown>;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}

/** Cresce ogni volta che il browser finisce di caricare dei font: le misure vanno rifatte. */
const useFontsVersion = (): number => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const fonts = (document as unknown as { fonts?: TFontFaceSet }).fonts;
    if (!fonts) return undefined;

    let alive = true;
    const bump = (): void => {
      if (alive) setVersion(v => v + 1);
    };
    fonts.ready.then(bump).catch(() => undefined);
    if (fonts.addEventListener) fonts.addEventListener("loadingdone", bump);

    return (): void => {
      alive = false;
      if (fonts.removeEventListener) fonts.removeEventListener("loadingdone", bump);
    };
  }, []);

  return version;
};

const sameHeights = (a: Map<number, number>, b: Map<number, number>): boolean => {
  if (a.size !== b.size) return false;
  let same = true;
  a.forEach((value, key) => {
    if (b.get(key) !== value) same = false;
  });
  return same;
};

/**
 * Altezza della card di ogni persona dell'organigramma, misurata sul testo reale.
 * La misura avviene prima del paint (useLayoutEffect), quindi la vista non "salta".
 */
export const useCardHeights = (
  hostRef: RefObject<HTMLElement>,
  index: Map<number, TOrgIndexEntry>,
  withActions: boolean,
): Map<number, number> => {
  const fontsVersion = useFontsVersion();
  const cacheRef = useRef({ fontsVersion: -1, heights: new Map<string, number>() });
  const [heights, setHeights] = useState<Map<number, number>>(() => new Map());

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    if (cacheRef.current.fontsVersion !== fontsVersion) {
      cacheRef.current = { fontsVersion, heights: new Map() };
    }

    const people = Array.from(index.values()).map(entry => entry.person);
    const next = measureCardHeights(host, people, withActions, cacheRef.current.heights);
    setHeights(prev => (sameHeights(prev, next) ? prev : next));
  }, [hostRef, index, withActions, fontsVersion]);

  return heights;
};
